import React, { useState } from 'react';

import { Button } from 'components/ui/button';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';

import { showSnackbar } from 'components/ui/snackbar/helper';
import BetaToggleInput from 'components/users/beta_program/beta_toggle_input';

import { put } from 'helpers/api';

import type { BetaProgramOptions, UserProfile } from 'components/users/types';

type Props = Pick<UserProfile, 'betaProgramOptions' | 'paths'>;

export default function BetaProgramForm({ betaProgramOptions, paths }: Props) {
  const [options, setOptions] = useState<BetaProgramOptions>(betaProgramOptions);
  const [saving, setSaving] = useState(false);

  function handleChange(key: keyof BetaProgramOptions, value: boolean) {
    setOptions(prevOptions => ({ ...prevOptions, [key]: value }));
  }

  async function saveBetaOptions(newOptions: BetaProgramOptions) {
    setSaving(true);

    try {
      await put(paths.update, { user: newOptions });
      showSnackbar({ text: 'Your preferences were saved.', variant: 'success' });
    } catch {
      showSnackbar({ text: 'Something went wrong saving your preferences.', variant: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  function handleSave() {
    saveBetaOptions(options);
  }

  function handleUnsubscribeAll() {
    const newOptions: BetaProgramOptions = {
      crmBetaProgram: false,
      crmFeedbackProgram: false,
      marketingBetaProgram: false,
      marketingFeedbackProgram: false,
      transactionManagementBetaProgram: false,
      transactionManagementFeedbackProgram: false,
    };

    setOptions(newOptions);
    saveBetaOptions(newOptions);
  }

  const anySubscribed = Object.values(options).some(Boolean);

  return (
    <>
      <h5 className="mb-2">Beta &amp; Feedback Program</h5>

      <div className="fs-large fw-light">Want to help us make our new releases the best they can be?</div>
      <div className="fs-large fw-light mt-6 mb-2">
        Sign up for our beta program or feedback program by selecting any of the following:
      </div>

      <Card className="mb-4">
        <Card.Body>
          <p>
            <span className="fw-semibold">Our Beta Program</span> will give you early access to new features that
            we're working on to test out and provide feedback early. Us learning how you use these new features or
            workflows will help influence what's released to everyone at the time of launch.
          </p>
          <BetaToggleInput
            checked={options.marketingBetaProgram}
            label="Marketing"
            onChange={checked => handleChange('marketingBetaProgram', checked)}
          />
          <BetaToggleInput
            checked={options.transactionManagementBetaProgram}
            label="Transaction Management"
            onChange={checked => handleChange('transactionManagementBetaProgram', checked)}
          />
          <BetaToggleInput
            checked={options.crmBetaProgram}
            label="CRM"
            onChange={checked => handleChange('crmBetaProgram', checked)}
          />
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <p>
            <span className="fw-semibold">Our Feedback Program</span> will give you an opportunity to chat directly
            with our product managers to help shape the future of Buildout. This could be to help identify key
            problems or workflows you think are missing or even providing your feedback on some market changes we're
            seeing. All feedback is good feedback.
          </p>
          <BetaToggleInput
            checked={options.marketingFeedbackProgram}
            label="Marketing"
            onChange={checked => handleChange('marketingFeedbackProgram', checked)}
          />
          <BetaToggleInput
            checked={options.transactionManagementFeedbackProgram}
            label="Transaction Management"
            onChange={checked => handleChange('transactionManagementFeedbackProgram', checked)}
          />
          <BetaToggleInput
            checked={options.crmFeedbackProgram}
            label="CRM"
            onChange={checked => handleChange('crmFeedbackProgram', checked)}
          />
        </Card.Body>
      </Card>

      <p className="fst-italic mb-2">NOTE: these features are not guaranteed</p>

      <div className="d-flex align-items-center">
        <Button disabled={saving} label="Save Changes" onClick={handleSave} />
        {anySubscribed && (
          <Button
            className="ms-3"
            disabled={saving}
            label="Unsubscribe from All"
            variant="secondary"
            onClick={handleUnsubscribeAll}
          />
        )}
      </div>
    </>
  );
}
